import { Tag, Sparkles, Copy, Gift, MessageCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveSiteOffers, useMyActiveOffers } from "@/lib/firestoreService";
import { useSEO } from "@/lib/useSEO";
import { track } from "@/lib/analytics";

function fmtExpiry(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export const OffersPage = () => {
  useSEO({
    title: "Offers & Discounts",
    description: "Check out the latest discount offers and coupon codes on Annapurna Home Pickles.",
  });
  const { user, profile } = useAuth();
  const siteOffers = useActiveSiteOffers();
  const myOffers = useMyActiveOffers(user?.id);

  const hasAny = siteOffers.length > 0 || myOffers.length > 0;

  return (
    <section data-testid="offers-page" className="py-10 sm:py-14 bg-[#F5E6CC] min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="brand-ribbon mb-3">Offers</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#5A2D1D]">
            Current <span className="italic text-[#7A3B25]">Offers</span>
          </h1>
        </div>

        <ReferralSection profile={profile} />

        {!hasAny ? (
          <div className="border border-[#5A2D1D]/20 rounded-sm p-12 text-center" data-testid="offers-empty">
            <Tag className="w-14 h-14 mx-auto mb-4 text-[#5A2D1D]/30" />
            <div className="font-display text-xl text-[#5A2D1D] mb-2">No offers right now</div>
            <p className="text-sm text-[#2E1A12]/60">Check back soon — we run promotions from time to time!</p>
          </div>
        ) : (
          <div className="space-y-10">
            {myOffers.length > 0 && (
              <div>
                <h2 className="font-display text-lg font-bold text-[#5A2D1D] mb-4 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#556B2F]" /> Just for you
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="my-offers-grid">
                  {myOffers.map((o) => <OfferCard key={o.id} offer={o} highlight />)}
                </div>
              </div>
            )}

            {siteOffers.length > 0 && (
              <div>
                <h2 className="font-display text-lg font-bold text-[#5A2D1D] mb-4 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#556B2F]" /> Discount Offers
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="site-offers-grid">
                  {siteOffers.map((o) => <OfferCard key={o.id} offer={o} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// Refer & Earn — moved here from Profile so it only lives in one place.
// Not part of the 2-up offers grid: it's a standalone promo block, signed-in
// customers only (referral codes are per-account).
const ReferralSection = ({ profile }) => {
  const code = profile?.referral_code;
  if (!code) return null;

  const link = `${window.location.origin}/?ref=${code}`;
  const shareText = `Try Annapurna Home Pickles! Use my code ${code} when you sign up and we both get 10% off: ${link}`;
  const waText = encodeURIComponent(shareText);
  // sms: URI — opens the phone's native SMS app pre-filled with the text,
  // same idea as the WhatsApp deep link. No recipient prefilled (that would
  // need a phone number we don't have), just the message body.
  const smsText = encodeURIComponent(shareText);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
    track("referral_code_copied", { referral_code: code });
  };

  return (
    <div data-testid="referral-section" className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 mb-8">
      <h2 className="font-display text-lg font-bold text-[#5A2D1D] flex items-center gap-2 mb-3">
        <Gift className="w-4 h-4 text-[#556B2F]" /> Refer & Earn
      </h2>
      <p className="text-sm text-[#2E1A12]/70 mb-4">
        Share your code — when a friend signs up with it, you both instantly get a 10% off coupon.
      </p>
      <div className="flex items-center gap-2 max-w-md">
        <div className="flex-1 px-4 py-2.5 rounded-sm bg-[#EFD9B4]/50 border border-[#5A2D1D]/20 font-mono font-bold text-[#5A2D1D] text-center tracking-[0.2em]">
          {code}
        </div>
        <button
          onClick={() => copy(code, "Code")}
          title="Copy code"
          className="flex-shrink-0 p-2.5 rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
        >
          <Copy className="w-4 h-4" />
        </button>
        <a
          href={`https://wa.me/?text=${waText}`}
          target="_blank"
          rel="noreferrer"
          title="Share on WhatsApp"
          onClick={() => track("referral_link_shared", { referral_code: code, share_method: "whatsapp" })}
          className="flex-shrink-0 p-2.5 rounded-sm bg-[#556B2F] hover:bg-[#6a8639] text-[#F5E6CC]"
        >
          <MessageCircle className="w-4 h-4" />
        </a>
        <a
          href={`sms:?body=${smsText}`}
          title="Share via SMS"
          onClick={() => track("referral_link_shared", { referral_code: code, share_method: "sms" })}
          className="flex-shrink-0 p-2.5 rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
        >
          <MessageSquare className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

const OfferCard = ({ offer, highlight }) => {
  const expiry = fmtExpiry(offer.expiry_date);
  return (
    <div
      data-testid={`offer-card-${offer.id}`}
      className={`relative rounded-sm p-4 sm:p-6 border ${
        highlight ? "bg-[#5A2D1D] text-[#F5E6CC] border-[#5A2D1D]" : "bg-white/60 text-[#2E1A12] border-[#5A2D1D]/20"
      }`}
    >
      <div className="font-display text-xl sm:text-2xl font-bold mb-1">{offer.discount_percent}% OFF</div>
      <div className={`font-semibold mb-2 ${highlight ? "text-[#F5E6CC]/90" : "text-[#5A2D1D]"}`}>{offer.name}</div>
      {offer.code && (
        <button
          onClick={() => { navigator.clipboard.writeText(offer.code); toast.success("Code copied!"); }}
          className={`inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-sm border font-mono text-xs font-bold tracking-wider ${
            highlight ? "border-[#F5E6CC]/40 text-[#F5E6CC] hover:bg-[#F5E6CC]/10" : "border-[#556B2F]/40 text-[#556B2F] hover:bg-[#556B2F]/10"
          }`}
          title="Copy code"
        >
          {offer.code} <Copy className="w-3 h-3" />
        </button>
      )}
      {expiry && (
        <div className={`text-xs ${highlight ? "text-[#F5E6CC]/60" : "text-[#2E1A12]/50"}`}>
          Valid until {expiry}
        </div>
      )}
    </div>
  );
};
